"use client";

import footballIcon from "@/assets/icons/sports/football.png";
import VolRect from "@/components/platform/vol-rect";
import { FootballMainGuessOptions } from "@/components/sport-event/FootballMainGuessOptions";
import { useTradePanel } from "@/components/trade-panel/hooks/use-trade-panel";
import { OrderType } from "@/components/trade-panel/types";
import { SmartImage } from "@/components/ui/smart-image";
import { type DOMInfo, useCollectDOMInfo } from "@/hooks/useCollectDOMInfo";
import { cn } from "@/lib/utils";
import { isEventExpired, isGuessExpired } from "@/utils/expiration";
import { formatLocalDate } from "@/utils/formatLocalDate";
import { navigateToEventDetail, useAppRouter } from "@/utils/router";
import { get } from "@/utils/utils";
import { BigMath, EventCategory } from "@yn/utils";
import { usePathname } from "next/navigation";
import { forwardRef, type MouseEvent, useImperativeHandle, useMemo, useState } from "react";

export interface FootballMainGuessEventCardRef {
    collectDOMInfo: () => DOMInfo;
}

export interface FootballMainGuessEventCardProps {
    className?: string;
    eventInfo: any;
    onCardClick?: () => void;
    orderType?: OrderType;
}

type FootballMainCardCenterState = { dateText: string; timeText: string; type: "upcoming" } | {
    awayScore: string;
    homeScore: string;
    secondaryText?: string;
    type: "score";
};

interface TeamInfo {
    imageUrl: string;
    title: string;
}

const FOOTBALL_STATUS_SHOW_HALF_TIME_SCORE = new Set( [ "HALF_TIME", "LIVE_2H", "ENDED" ] );
const FOOTBALL_STATUS_SHOW_REGULAR_TIME_SCORE = new Set( [
    "LIVE_ET_1H",
    "LIVE_ET_2H",
    "PENALTY_SHOOTOUT",
    "ENDED_AET",
    "ENDED_AP",
] );

function hasDisplayValue( value: unknown ) {
    return value !== null && value !== undefined && value !== "";
}

function getGuessList( eventInfo: any ) {
    const rawGuessList = get( eventInfo, "guessList", [] );
    if ( !Array.isArray( rawGuessList ) ) return [];

    return rawGuessList;
}

function isMainGuess( guess: any ) {
    return Number( get( guess, "mainFlag", 0 ) ) === 1;
}

export function hasFootballMainGuess( eventInfo: any ) {
    const eventCategory = Number( get( eventInfo, "eventCategory", EventCategory.Normal ) );
    if ( eventCategory !== EventCategory.Football ) return false;

    const guessList = getGuessList( eventInfo );
    return guessList.length > 1 && guessList.some( isMainGuess );
}

function getTeamInfo( eventInfo: any, index: number ): TeamInfo {
    const matchup = get( eventInfo, `matchups.${index}`, {} );

    return {
        imageUrl: get( matchup, "matchupImageUrl", "" ),
        title: get( matchup, "title", index === 0 ? "Team 1" : "Team 2" ),
    };
}

function getFootballStatus( eventInfo: any ) {
    const homeStatus = get( eventInfo, "matchups.0.footballStatus", "" );
    const awayStatus = get( eventInfo, "matchups.1.footballStatus", "" );
    const status = hasDisplayValue( homeStatus ) ? homeStatus : awayStatus;

    return hasDisplayValue( status ) ? String( status ) : "";
}

function resolveScoreSecondaryText( eventInfo: any ) {
    const footballStatus = getFootballStatus( eventInfo );
    const homeHalfScore = get( eventInfo, "matchups.0.halfScore", null );
    const awayHalfScore = get( eventInfo, "matchups.1.halfScore", null );

    if ( FOOTBALL_STATUS_SHOW_REGULAR_TIME_SCORE.has( footballStatus ) ) {
        const homeSecondHalfScore = get( eventInfo, "matchups.0.secondHalfScore", null );
        const awaySecondHalfScore = get( eventInfo, "matchups.1.secondHalfScore", null );

        if (
            !hasDisplayValue( homeHalfScore )
            || !hasDisplayValue( awayHalfScore )
            || !hasDisplayValue( homeSecondHalfScore )
            || !hasDisplayValue( awaySecondHalfScore )
        ) return undefined;

        const homeRegularTimeScore = BigMath.plus( homeHalfScore, homeSecondHalfScore, 0 );
        const awayRegularTimeScore = BigMath.plus( awayHalfScore, awaySecondHalfScore, 0 );

        return `90M ${homeRegularTimeScore}-${awayRegularTimeScore}`;
    }

    if ( FOOTBALL_STATUS_SHOW_HALF_TIME_SCORE.has( footballStatus ) ) {
        if ( !hasDisplayValue( homeHalfScore ) || !hasDisplayValue( awayHalfScore ) ) return undefined;

        return `HT ${homeHalfScore}-${awayHalfScore}`;
    }

    return undefined;
}

function resolveCenterState( eventInfo: any ): FootballMainCardCenterState {
    const matchup1Score = get( eventInfo, "matchups.0.matchRecord", "" );
    const matchup2Score = get( eventInfo, "matchups.1.matchRecord", "" );

    if ( hasDisplayValue( matchup1Score ) && hasDisplayValue( matchup2Score ) ) {
        return {
            awayScore: String( matchup2Score ),
            homeScore: String( matchup1Score ),
            secondaryText: resolveScoreSecondaryText( eventInfo ),
            type: "score",
        };
    }

    const occurTime = get( eventInfo, "occurTime", "" );
    return {
        dateText: hasDisplayValue( occurTime ) ? formatLocalDate( occurTime, "MMM D" ) : "",
        timeText: hasDisplayValue( occurTime ) ? formatLocalDate( occurTime, "h:mm A" ) : "-",
        type: "upcoming",
    };
}

function FootballTeam( { team }: { team: TeamInfo } ) {
    const fallbackText = team.title ? team.title.substring( 0, 3 ).toUpperCase() : "TBD";

    return (
        <div className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded bg-surface-subtle">
                {team.imageUrl
                    ? (
                        <SmartImage
                            src={team.imageUrl}
                            alt={team.title}
                            className="h-full w-full object-contain"
                            imageUrlOptions={{ width: 150, height: 150 }}
                        />
                    )
                    : <span className="text-xs font-bold text-t3">{fallbackText}</span>}
            </div>
            <div className="w-full truncate text-center text-xs font-medium text-t1">{team.title}</div>
        </div>
    );
}

function FootballCenter( { state }: { state: FootballMainCardCenterState } ) {
    if ( state.type === "upcoming" ) {
        return (
            <div className="flex w-24 shrink-0 flex-col items-center gap-2 text-center">
                <div className="max-w-full truncate font-din text-xl font-black leading-none text-t1">
                    {state.timeText}
                </div>
                {state.dateText
                    ? <div className="text-xs font-medium leading-none text-t3">{state.dateText}</div>
                    : null}
            </div>
        );
    }

    return (
        <div className="flex w-24 shrink-0 flex-col items-center gap-2 text-center">
            <div className="flex items-center justify-center gap-2 whitespace-nowrap leading-none text-t1">
                <span className="font-din text-2xl font-black">{state.homeScore}</span>
                <span className="font-din text-base font-bold text-t3">-</span>
                <span className="font-din text-2xl font-black">{state.awayScore}</span>
            </div>
            {state.secondaryText
                ? <div className="text-xs font-medium leading-none text-t3">{state.secondaryText}</div>
                : null}
        </div>
    );
}

export const FootballMainGuessEventCard = forwardRef<FootballMainGuessEventCardRef, FootballMainGuessEventCardProps>(
    function FootballMainGuessEventCard( { className, eventInfo, onCardClick, orderType }, ref ) {
        const router = useAppRouter();
        const pathname = usePathname();
        const { open } = useTradePanel();
        const [ , setForceUpdate ] = useState( 0 );
        const { collectDOMInfo, registerButton } = useCollectDOMInfo();

        useImperativeHandle( ref, () => ( { collectDOMInfo } ) );

        const homeTeam = useMemo( () => getTeamInfo( eventInfo, 0 ), [ eventInfo ] );
        const awayTeam = useMemo( () => getTeamInfo( eventInfo, 1 ), [ eventInfo ] );
        const centerState = useMemo( () => resolveCenterState( eventInfo ), [ eventInfo ] );
        const guessRows = useMemo( () => getGuessList( eventInfo ), [ eventInfo ] );

        const eventEnded = isEventExpired( eventInfo );
        const title = get( eventInfo, "leagueName", "" ) || get( eventInfo, "title", "" )
            || get( eventInfo, "eventTitle", "" ) || "-";

        const handleCardClick = () => {
            if ( onCardClick ) {
                onCardClick();
                return;
            }

            const eventCategory = get( eventInfo, "eventCategory", undefined );
            void navigateToEventDetail( router, {
                eventNo: get( eventInfo, "eventNo", "" ),
                isMainPageEvent: true,
                ...( eventCategory !== undefined && { eventCategory } ),
            } );
        };

        const handleTradeClick = ( e: MouseEvent<HTMLButtonElement>, guess: any, selectedOption: "yes" | "no" ) => {
            e.stopPropagation();
            if ( eventEnded || isGuessExpired( guess ) ) return;

            open( {
                detailInfo: eventInfo,
                eventInfo,
                guessInfo: guess,
                selectedOption,
                tradeType: "buy",
                pathname,
                ...( orderType ? { orderType } : {} ),
            } );
        };

        return (
            <div
                className={cn(
                    "flex w-full cursor-pointer flex-col gap-3 rounded-xl border border-stroke bg-surface-card p-3 shadow-[0_8px_16px_0_rgba(0,0,0,0.04)]",
                    className,
                )}
                onClick={handleCardClick}
            >
                <div className="flex w-full flex-col gap-3">
                    <div className="flex w-full items-center gap-2">
                        <SmartImage src={footballIcon} alt="" className="size-6 shrink-0" />
                        <div className="min-w-0 flex-1 truncate text-xs font-medium text-t3">{title}</div>
                    </div>

                    <div className="flex w-full items-center gap-2">
                        <FootballTeam team={homeTeam} />
                        <FootballCenter state={centerState} />
                        <FootballTeam team={awayTeam} />
                    </div>

                    {guessRows.length > 0
                        ? (
                            <FootballMainGuessOptions
                                displayMode="aggregation"
                                eventEnded={eventEnded}
                                guesses={guessRows}
                                registerButton={registerButton}
                                onTradeClick={handleTradeClick}
                            />
                        )
                        : null}
                </div>

                <VolRect
                    eventInfo={eventInfo}
                    showLiveLabel={false}
                    onCountdownEnd={() => setForceUpdate( ( prev ) => BigMath.plus( prev, 1, 0 ) )}
                />
            </div>
        );
    },
);
